import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridCustomRowProps,
  GridCustomHeaderCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid';
import { PagerProps } from '@progress/kendo-react-data-tools';
import { IconButton, Button, Tooltip } from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Columns } from '@/types';
import { getEquipmentColumnsFromAssemblyTemplate, updateUrl } from '@/utils';
import { getPoiIconsColumn } from '@/utils/getPoiColumns';
import { useHavePermission, useRouter } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  Assembly,
  useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery,
  useGetUserPreferencesQuery,
} from '@/__generated__/graphql';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR,
  INSIGHTS_USER_PREFERENCE_ERROR,
} from '@/constants/Errors';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import useLiveSyncAllPois from '../../shared/hooks/useLiveSyncAllPois';
import { useKendoInlineEdit } from '@/hooks/useKendoInlineEdit';
import { EditCellModal } from '../../shared/EquipmentTable/EditCellModal';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { KendoPager } from '@/components/shared/KendoGrid';
import { getColumnDimensions } from './utils';
import { CustomHeaderCell } from './CustomHeaderCell';
import { CustomCell } from './CustomCell';
import './KendoEquipmentTable.css';
import { SaveViewModal } from './SaveViewModal';
import InspectionPlansPills from './InspectionPlansPills';
import { ColumnModal } from './ColumnModal';
import { LoadViewModal } from '../LoadViewModal/LoadViewModal';
import { Permissions } from '@/types';
import { RowRender } from './RowRender';
type Props = {
  saveSingleEdit?: (itemId: string, columnKey: string, value: unknown) => Promise<void>;
  saveBulkEdit?: (columnKey: string, value: unknown, itemIds: string[]) => Promise<void>;
};

const MyPager = (props: PagerProps) => {
  return (
    <div className="kendo-equipment-table-footer">
      <KendoPager
        skip={props.skip}
        take={props.take}
        total={props.total}
        onPageChange={props.onPageChange}
        pageSizes={[10, 20, 50, 100]}
        buttonCount={5}
        info={true}
        previousNext={true}
        responsive={true}
      />
    </div>
  );
};

const getView3DColumn = (
  router: ReturnType<typeof useRouter>,
  structureId: string,
  setSelectedAssemblyId: (id: string | undefined) => void
): Columns => ({
  isSortable: false,
  visibility: true,
  key: 'view3D',
  columnName: 'View 3D',
  displayConfig: {
    type: '3DBUTTON',
  },
  onCellClick: (event, item) => {
    event.stopPropagation();
    setSelectedAssemblyId(undefined);
    const url = `/analysis/${structureId}/viewer?assembly=${item?.tagName}`;
    const isCtrlClick = event.ctrlKey || event.metaKey;
    if (isCtrlClick) window.open(url, '_blank');
    else router.push(url);
  },
});

export const KendoEquipmentTable = ({ saveSingleEdit, saveBulkEdit }: Props) => {
  const [page, setPage] = useState(0);
  const [allColumns, setAllColumns] = useState<Columns[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const setInsightsExplorerLoading = useSetAtom(state.insightsExplorerLoadingState);
  const [allAssemblies, setAllAssemblies] =
    useAtom(state.allAssembliesForInsights);
  const [assembliesCount, setAssembliesCount] = useAtom(state.assembliesCount);
  const [, setLoadingTable] = useState(true);
  const setAssembliesLoading = useSetAtom(state.assembliesLoading);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useAtom(state.selectedAssemblyIds);
  const [dialogOpen, setDialogOpen] = useState(false);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const templateId = useSelectedAssemblyTemplateId()!;
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const filteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const setAppliedAssemblyFilters = useSetAtom(state.appliedAssemblyFilters);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(filteredDeckIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);
  const setAllComponentsSelected = useSetAtom(state.isAllComponentsSelected);
  const { showSnackBar } = useSnackBarMessage({});
  const [headerEditOpen, setHeaderEditOpen] = useState(false);
  const [headerEditColumnKey, setHeaderEditColumnKey] = useState<string>('');
  const [headerEditColumnName, setHeaderEditColumnName] = useState<string>('');
  const [headerEditColumnMeta, setHeaderEditColumnMeta] = useState<Columns | null>(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [loadViewOpen, setLoadViewOpen] = useState(false);
  useEffect(() => {
    setAppliedPaintRegionFilters(selectedPaintRegionIds);
    setAppliedAssemblyFilters((prev) => ({
      ...prev,
      paintRegionFilters: selectedPaintRegionIds,
    }));
  }, [selectedPaintRegionIds, setAppliedAssemblyFilters]);

  const [applyFilters] = useAtom(state.applyFilters);
  const [idmsUploadSequence] = useAtom(state.idmsUploadSequence);
  const structureId = useGetStructureId();
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const {
    data: userPreferenceData,
    error: userPreferenceError,
    loading: userPreferenceLoading,
  } = useGetUserPreferencesQuery({
    fetchPolicy: 'cache-first',
  });
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const { getUnitSuffix } = useUnitConversion();
  const { edit, setEdit, handleEditChange, exitEdit } = useKendoInlineEdit();

  useLiveSyncAllPois(() => {
    if (structureId && templateId) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  });

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setAllAssemblies([]);
        setAllAssemblies(data?.getAssembliesByTemplateFieldsFilters);
        setAssembliesCount(data?.getAssembliesCount?.count ?? 0);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
        setAssembliesLoading(false);
      },
      onError: (err: Error) => {
        ErrorLogger.log(
          INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR.withContext({
            input: {
              selectedTemplateFilters: appliedTemplateFilters,
              structureId,
              templateId,
              selectedDeckIds: appliedDeckFilters,
              selectedPaintRegionIds: appliedPaintRegionFilters,
              selectedWorkpackIds: appliedWorkpackFilters,
            },
            pageInfo: {
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            },
          }).withError(err)
        );
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
        setAssembliesLoading(false);
      },
    });

  const getFilteredAssemblies = useCallback(() => {
    if (structureId && templateId) {
      setAssembliesLoading(true);
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  }, [
    appliedDeckFilters,
    appliedPaintRegionFilters,
    appliedTemplateFilters,
    appliedWorkpackFilters,
    fetchFilteredAssemblies,
    page,
    rowsPerPage,
    setAssembliesLoading,
    sortBy,
    sortDirection,
    structureId,
    templateId,
  ]);
  // Refresh table whenever an IDMS upload completes
  useEffect(() => {
    if (templateId) getFilteredAssemblies();
  }, [idmsUploadSequence, getFilteredAssemblies, templateId]);

  useEffect(() => {
    if (templateId && applyFilters) {
      getFilteredAssemblies();
    }
  }, [applyFilters, getFilteredAssemblies, templateId]);

  useEffect(() => {
    getFilteredAssemblies();
  }, [getFilteredAssemblies, rowsPerPage, page]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(filteredDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
      setPage(0);
      setAppliedAssemblyFilters((prev) => ({
        ...prev,
        templateFilters: selectedTemplateFilters,
        deckFilters: filteredDeckIds,
        paintRegionFilters: selectedPaintRegionIds,
        workpackFilters: selectedWorkpackIds,
      }));
    }
  }, [
    templateId,
    selectedTemplateFilters,
    filteredDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    applyFilters,
    fetchFilteredAssemblies,
    structureId,
    page,
    rowsPerPage,
    setAppliedAssemblyFilters,
  ]);

  const router = useRouter();
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS)

  useEffect(() => {
    const assemblyFromUrl = router.query?.assembly;
    if (assemblyFromUrl && allAssemblies?.length) {
      const found = allAssemblies.find((a) => a.tagName === assemblyFromUrl);
      if (found) {
        setSelectedAssemblyId(found.id);
        setSelectedAssemblyName(found.tagName);
      }
    }
  }, [router.query?.assembly, allAssemblies, setSelectedAssemblyId, setSelectedAssemblyName]);

  const initialColumns = useMemo(() => {
    if (!assemblyTemplate) return [];
    const allTemplateColumns = getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
    const { plannerColumns, columnsFromTemplate } = allTemplateColumns.reduce<{
      plannerColumns: Columns[];
      columnsFromTemplate: Columns[];
    }>(
      (acc, column) => {
        if (column.key.includes('planner')) {
          acc.plannerColumns.push(column);
        } else {
          acc.columnsFromTemplate.push(column);
        }
        return acc;
      },
      { plannerColumns: [], columnsFromTemplate: [] }
    );
    const poiIconsColumn = getPoiIconsColumn();
    const view3dColumn = [getView3DColumn(router, structureId, setSelectedAssemblyId)];
    return [
      {
        isSortable: true,
        visibility: true,
        key: 'tagName',
        columnName: 'Component',
      },
      ...columnsFromTemplate,
      poiIconsColumn,
      ...plannerColumns,
      ...view3dColumn,
    ] as Columns[];
  }, [assemblyTemplate, router, structureId, setSelectedAssemblyId]);

  useEffect(() => {
    setAllColumns((prevColumns) => {
      if (visibleColumns.length > 0) {
        return initialColumns.map((column) => ({
          ...column,
          visibility: visibleColumns.includes(column.columnName),
        }));
      }
      if (prevColumns.length === 0 || prevColumns.length !== initialColumns.length) {
        const defaultColumns = new Set(
          initialColumns.filter(({ visibility }) => visibility).map(({ columnName }) => columnName)
        );
        return initialColumns.map((column) => ({
          ...column,
          visibility: defaultColumns.has(column.columnName),
        }));
      }
      return initialColumns.map((column) => {
        const existingColumn = prevColumns.find((c) => c.columnName === column.columnName);
        return {
          ...column,
          visibility: existingColumn?.visibility ?? column.visibility,
        };
      });
    });
  }, [initialColumns, visibleColumns]);

  useEffect(() => {
    if (userPreferenceLoading) {
      return;
    }
    if (userPreferenceError) {
      ErrorLogger.log(
        INSIGHTS_USER_PREFERENCE_ERROR.withContext({ structureId }).withError(userPreferenceError)
      );
    }
    const preferencesList = userPreferenceData?.getUserPreferences?.structure?.[structureId];
    const userColumns = preferencesList?.columns?.COMPONENT ?? [];
    let nextVisible =
      userColumns.length > 0
        ? [...userColumns]
        : initialColumns
          .filter((c) => c.visibility)
          .slice(0, 8)
          .map((c) => c.columnName);
    if (!nextVisible.includes('Component')) {
      nextVisible = ['Component', ...nextVisible];
    }
    setVisibleColumns(nextVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    structureId,
    userPreferenceData?.getUserPreferences?.structure,
    userPreferenceError,
    userPreferenceLoading,
  ]);

  const toggleDialog = useCallback(() => {
    setDialogOpen(!dialogOpen);
  }, [dialogOpen]);

  const handleSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(property);
    },
    [sortDirection, sortBy]
  );

  const activeColumns = useMemo(() => {
    return allColumns
      .filter(({ visibility }) => visibility)
      .map((column) => {
        const { displayUnit, unit, columnName, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({
          unit: displayUnit ?? unit,
          field: templateFieldKey,
        });
        let upperCaseColumnName = columnName.toUpperCase();
        upperCaseColumnName = unitSuffix
          ? `${upperCaseColumnName} (${unitSuffix})`
          : upperCaseColumnName;
        return { ...column, columnName: upperCaseColumnName };
      });
  }, [allColumns, getUnitSuffix]);

  const dialogColumns = useMemo(() => {
    return allColumns.map(
      ({ columnName, visibility, displayUnit, unit, templateFieldKey, enableInlineEdit }) => {
        return {
          name: columnName,
          key: columnName,
          isSelected: visibility,
          displayUnit: displayUnit ?? '',
          unit: unit ?? '',
          templateFieldKey,
          enableInlineEdit,
        };
      }
    );
  }, [allColumns]);

  const handleApplyColumns = useCallback((selectedColumnsKeys: string[]) => {
    setVisibleColumns(selectedColumnsKeys);
    const selectedColumnsSet = new Set(selectedColumnsKeys);
    setAllColumns((previous) =>
      previous.map((column) => ({
        ...column,
        visibility: selectedColumnsSet.has(column.columnName ?? ''),
      }))
    );
  }, []);

  const handlePageChange = useCallback((event: GridPageChangeEvent) => {
    const newSkip = event.page?.skip ?? 0;
    const newTake = event.page?.take ?? rowsPerPage;
    const newPage = Math.floor(newSkip / newTake);
    setPage(newPage);
    if (newTake !== rowsPerPage) {
      setRowsPerPage(newTake);
    }
  }, [rowsPerPage]);

  const handleRowCheckBoxChange = useCallback(
    (assemblyId: string) => {
      setSelectedAssemblyIds((prev) => {
        if (prev.includes(assemblyId)) {
          return prev.filter((id) => id !== assemblyId);
        }
        return [...prev, assemblyId];
      });
    },
    [setSelectedAssemblyIds]
  );

  const headerCheckBoxChange = useCallback(() => {
    if (!allAssemblies) return;
    const allIds = allAssemblies.map((a) => a.id);
    if (selectedAssemblyIds.length === allIds.length) {
      setSelectedAssemblyIds([]);
      setAllComponentsSelected(false);
    } else {
      setSelectedAssemblyIds(allIds);
      setAllComponentsSelected(true);
    }
  }, [allAssemblies, selectedAssemblyIds.length, setSelectedAssemblyIds, setAllComponentsSelected]);

  const handleRowClick = useCallback(
    (dataItem: Assembly) => {
      setSelectedAssemblyId(dataItem.id);
      setSelectedAssemblyName(dataItem.tagName);
      updateUrl('assembly', dataItem.tagName)
    },
    [setSelectedAssemblyId, setSelectedAssemblyName]
  );

  const onOpenHeaderEditModal = useCallback((columnKey: string, columnName: string) => {
    const columnMeta = allColumns.find((c) => String(c.key) === String(columnKey)) ?? null;
    setHeaderEditColumnKey(columnKey);
    setHeaderEditColumnName(columnName);
    setHeaderEditColumnMeta(columnMeta);
    setHeaderEditOpen(true);
  }, [allColumns]);


  const handleSaveSingleEdit = useCallback(
    async (itemId: string, columnKey: string, value: unknown) => {
      setAllAssemblies((prev = []) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const idx = list.findIndex((a) => !!a && a.id === itemId);
        if (idx === -1) return prev;

        const assembly = list[idx];
        const existingTemplate =
          assembly && assembly.templateFieldData && typeof assembly.templateFieldData === 'object'
            ? assembly.templateFieldData
            : {};

        const newTemplate = { ...existingTemplate, [columnKey]: value };
        const updated = { ...assembly, templateFieldData: newTemplate };

        list[idx] = updated;
        return list;
      });
      if (saveSingleEdit) {
        await saveSingleEdit(itemId, columnKey, value);
      }
    },
    [saveSingleEdit, setAllAssemblies]
  );

  const isCheckedAll = useMemo(() => {
    if (!allAssemblies || allAssemblies.length === 0) return false;
    return selectedAssemblyIds.length === allAssemblies.length;
  }, [allAssemblies, selectedAssemblyIds.length]);

  const toggleAddColumn = useCallback(() => {
    setAddColumnOpen((s) => !s);
  }, []);

  const handleCreateColumn = useCallback((newColumn: Columns) => {
    setVisibleColumns((prev) => (prev.includes(newColumn.columnName) ? prev : [...prev, newColumn.columnName]));
  }, []);

  const CustomRow = useMemo(
    () => (props: GridCustomRowProps) => <RowRender {...props} exitEdit={exitEdit} />,
    [exitEdit]
  );
  const canEditCells = isAdmin || canManageInspectionPlans;
  const editableCellTypes = useMemo(
    () => (props: GridCustomCellProps) => (
      <CustomCell
        {...props}
        onSave={handleSaveSingleEdit}
        exitEdit={exitEdit}
        columns={activeColumns}
        canEdit={canEditCells}
        isEditMode={true}
      />
    ),
    [handleSaveSingleEdit, exitEdit, activeColumns, canEditCells]
  );

  return (
    <>
      <EditCellModal
        open={headerEditOpen}
        onClose={() => setHeaderEditOpen(false)}
        saveBulkEdit={async (newValue: unknown) => {
          if (saveBulkEdit && headerEditColumnKey) {
            await saveBulkEdit(headerEditColumnKey, newValue, selectedAssemblyIds);
          }
        }}
        cellData={{ columnName: headerEditColumnName, columnKey: headerEditColumnKey, cellType: headerEditColumnMeta?.displayConfig?.type, options: headerEditColumnMeta?.options }}
        onSuccess={() => {
          showSnackBar('Table values are edited successfully', 'success');
          getFilteredAssemblies();
        }}
        onError={() => showSnackBar('Failed to edit table values', 'error')}
      />

      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Enable the columns you would like to see in the table"
        subHeading="Select the columns you want to display:"
        applyButtonText="Apply"
        userPreferenceKey="COMPONENT"
        userPreferenceType="columns"
        onApply={handleApplyColumns}
        onClose={() => setDialogOpen(false)}
      />
      <LoadViewModal open={loadViewOpen} handleCloseView={() => setLoadViewOpen(false)} />
      <ColumnModal
        open={addColumnOpen}
        initialColumns={initialColumns}
        onClose={() => toggleAddColumn()}
        onCreate={(col) => handleCreateColumn(col)}
      />
      <div className="kendo-equipment-table-wrapper">
        <div className="kendo-equipment-table-controls" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InspectionPlansPills />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                toggleAddColumn();
              }}
              aria-label="add-new-column"
              sx={{ textTransform: 'none', borderRadius: 1, px: 1.5 }}
            >
              Add New Column
            </Button>
            {(isAdmin || canManageInspectionPlans) && (
              <Tooltip title="Save view" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSaveViewOpen(true);
                  }}
                  aria-label="save-view"
                  sx={{ p: 1 }}
                >
                  <AbyssIcon iconName="save-view" style={{ width: '2rem', height: '2rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || canManageInspectionPlans) && (
              <Tooltip title="Load view" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setLoadViewOpen(true);
                  }}
                  aria-label="load-view"
                  sx={{ p: 1 }}
                >
                  <AbyssIcon iconName="load-view" style={{ width: '2rem', height: '2rem' }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleDialog();
              }}
              sx={{ p: 1 }}
              aria-label="table-settings"
            >
              <AbyssIcon iconName="gear" style={{ width: '2rem', height: '2rem' }} />
            </IconButton>
          </div>
        </div>
        <SaveViewModal
          open={saveViewOpen}
          onClose={() => setSaveViewOpen(false)}
          selectedTemplateFilters={selectedTemplateFilters}
          selectedDeckIds={filteredDeckIds}
          selectedWorkpackIds={selectedWorkpackIds}
          selectedPaintRegionIds={selectedPaintRegionIds}
        />
        <div className="kendo-equipment-table-header-row">
          <div className="kendo-equipment-table-container">
            <Grid
              data={allAssemblies || []}
              // assembly id
              dataItemKey="id"
              skip={page * rowsPerPage}
              take={rowsPerPage}
              total={assembliesCount}
              pageable={true}
              onPageChange={handlePageChange}
              pager={MyPager}
              sortable={false}
              resizable={true}
              className="kendo-equipment-grid"
              onEditChange={handleEditChange}
              rows={{ data: CustomRow }}
              onRowClick={(e) => handleRowClick(e.dataItem)}
              edit={edit}
              cells={{
                edit: {
                  text: editableCellTypes,
                  boolean: editableCellTypes,
                  numeric: editableCellTypes,
                },
              }}
              editable={{ mode: 'incell' }}
            >
              {activeColumns.map((column, index) => {
                const dimensions = getColumnDimensions(column, activeColumns.length);
                const isFirstColumn = index === 0;
                const isLastColumn = index === activeColumns.length - 1;

                const HeaderCellComponent = (props: GridCustomHeaderCellProps) => (
                  <CustomHeaderCell
                    {...props}
                    column={column}
                    columnIndex={index}
                    totalColumns={activeColumns.length}
                    isFirstColumn={isFirstColumn}
                    isLastColumn={isLastColumn}
                    onSort={handleSort}
                    onToggleColumnDialog={toggleDialog}
                    onHeaderCheckboxChange={headerCheckBoxChange}
                    onOpenHeaderEditModal={onOpenHeaderEditModal}
                    selectedRowIds={selectedAssemblyIds}
                    isChecked={isCheckedAll}
                    orderBy={sortBy}
                    order={sortDirection}
                  />
                );

                const CellComponent = (props: GridCustomCellProps) => {
                  return (
                    <CustomCell
                      {...props}
                      column={column}
                      totalColumns={activeColumns.length}
                      isFirstColumn={isFirstColumn}
                      isSelected={props.dataItem.id === selectedAssemblyId}
                      isChecked={selectedAssemblyIds.includes(props.dataItem.id)}
                      onCheckboxChange={() => handleRowCheckBoxChange(props.dataItem.id)}
                      onCellClick={(e, item) => {
                        if (column.onCellClick) {
                          column.onCellClick(e, item);
                        }
                      }}
                      saveSingleEdit={handleSaveSingleEdit}
                      setEdit={setEdit}
                      canEdit={canEditCells}
                    />
                  );
                };

                const cellType = column.displayConfig?.type;
                const isKendoIncellEditable =
                  !!column.enableInlineEdit && cellType !== 'DATE' && cellType !== 'DROPDOWN';

                return (
                  <GridColumn
                    key={column.key}
                    field={column.key}
                    title={column.columnName}
                    width={dimensions.width}
                    editable={isKendoIncellEditable || undefined}
                    cells={{
                      headerCell: HeaderCellComponent,
                      data: CellComponent,
                    }}
                  />
                );
              })}
            </Grid>
          </div>
        </div>
      </div>
    </>
  );
};
